import React from 'react';
import { PeersonaSafetyResource } from '../../hooks/api/usePeersonaChat';
import {
  PEERSONA_SAFETY_ACCENT,
  PEERSONA_SAFETY_CARD_BG,
  PEERSONA_SAFETY_CARD_BORDER,
  PEERSONA_SAFETY_LABEL,
} from './peersona-styles';

interface Props {
  resources: PeersonaSafetyResource[];
}

/**
 * Renders the safety-resource block attached beneath the Peersona's reply
 * on crisis-flagged turns. The model's reply is instructed (via the
 * SAFETY block injected into the prompt) to briefly reference these
 * cards, so the visual attachment and the conversational acknowledgement
 * line up — the user shouldn't see a floating card that the reply
 * doesn't mention.
 *
 * Resources are heterogeneous: a hotline has a phone number, an article
 * has a URL. Render whichever CTAs are present per resource; never
 * render an empty card.
 */
const formatPhoneHref = (phone: string): string => `tel:${phone.replace(/[^\d+]/g, '')}`;

export const PeersonaSafetyCard: React.FC<Props> = ({ resources }) => {
  if (!resources?.length) return null;

  return (
    <div
      className="mt-2 flex flex-col gap-3 rounded-2xl px-4 py-3 shadow-sm"
      style={{
        backgroundColor: PEERSONA_SAFETY_CARD_BG,
        border: `1px solid ${PEERSONA_SAFETY_CARD_BORDER}`,
      }}
    >
      <p
        className="font-albert-sans text-[11px] font-semibold uppercase tracking-wide"
        style={{ color: PEERSONA_SAFETY_ACCENT }}
      >
        {PEERSONA_SAFETY_LABEL}
      </p>
      <ul className="flex flex-col gap-3">
        {resources.map((res, idx) => (
          <li key={res.id ?? `${res.title}-${idx}`} className="flex flex-col gap-1.5">
            <p className="font-albert-sans text-sm font-semibold text-gray-900">{res.title}</p>
            {res.description ? (
              <p className="font-open-sans line-clamp-3 text-xs text-gray-600">{res.description}</p>
            ) : null}
            {res.phone || res.url ? (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {res.phone ? (
                  <a
                    href={formatPhoneHref(res.phone)}
                    className="font-albert-sans inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: PEERSONA_SAFETY_ACCENT }}
                  >
                    Call {res.phone}
                  </a>
                ) : null}
                {res.url ? (
                  <a
                    href={res.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-albert-sans inline-flex items-center justify-center rounded-full border px-3 py-1 text-xs font-semibold transition-colors hover:bg-white"
                    style={{
                      borderColor: PEERSONA_SAFETY_ACCENT,
                      color: PEERSONA_SAFETY_ACCENT,
                    }}
                  >
                    Open resource
                  </a>
                ) : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};
